import addYears from 'date-fns/addYears'
import addDays from 'date-fns/addDays'
import endOfYear from 'date-fns/endOfYear'
import { TimeIncidence } from '~/packages/general/variable/spaceTime.types'
import { parseDate, stringifyDate } from '~/prix/types/date'

type TimeSeriesPreviewResultItem = {
  dateTime: string
  value?: number | null
  isPrediction?: boolean | null
}

type NormalizedPreviewItem = {
  dateTime: string
  value: number
  isPrediction: boolean
}

export function buildCompleteTimeSeriesPreviewTime(
  options: {
    isPredicted: boolean
  },
  now: Date = new Date(),
): TimeIncidence | null {
  const previewEndDate = options.isPredicted
    ? stringifyDate(endOfYear(addYears(now, 1)))
    : stringifyDate(now)

  return {
    type: options.isPredicted ? 'customDateRangePredicted' : 'customDateRange',
    title: options.isPredicted
      ? 'Série temporal completa até o fim do ano que vem'
      : 'Série temporal completa até hoje',
    startDate: undefined,
    endDate: previewEndDate,
    groupType: 'daily',
  }
}

export function buildCompleteTimeSeriesPreviewSeries(
  items: TimeSeriesPreviewResultItem[] | null | undefined,
  options: {
    endDate: string
  },
) {
  if (!items?.length) {
    return {
      values: [] as number[],
      highlights: [] as boolean[],
      predictions: [] as boolean[],
      dates: [] as string[],
    }
  }

  const inputItems = items.map(item => ({
    dateTime: normalizePreviewDate(item.dateTime),
    value: Number(item.value ?? 0),
    isPrediction: Boolean(item.isPrediction),
  }))
  const valuesByDate = new Map<string, { value: number; isPrediction: boolean }>()

  for (const item of inputItems) {
    valuesByDate.set(item.dateTime, {
      value: item.value,
      isPrediction: item.isPrediction,
    })
  }

  const startDate = inputItems.reduce(
    (currentStartDate, item) =>
      item.dateTime < currentStartDate ? item.dateTime : currentStartDate,
    inputItems[0].dateTime,
  )
  const normalizedItems: NormalizedPreviewItem[] = []
  const endDate = parseDate(options.endDate)

  for (
    let currentDate = parseDate(startDate);
    currentDate <= endDate;
    currentDate = addDays(currentDate, 1)
  ) {
    const dateString = stringifyDate(currentDate)
    const item = valuesByDate.get(dateString)
    normalizedItems.push({
      dateTime: dateString,
      value: item?.value ?? 0,
      isPrediction: item?.isPrediction ?? false,
    })
  }

  return {
    values: normalizedItems.map(item => item.value),
    highlights: normalizedItems.map(() => false),
    predictions: normalizedItems.map(item => item.isPrediction),
    dates: normalizedItems.map(item => item.dateTime),
  }
}

function normalizePreviewDate(dateTime: string) {
  return dateTime.slice(0, 10)
}
